/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  Directive,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { isObservable } from '@gh/core-util';
import { distinctUntilChanged$ } from '@gh/rx/operators';
import { CellFocusedEvent, Column, GridApi, GridOptions, IRowNode } from 'ag-grid-community';
import { getDisplayedNeighborRowForSelection, setGridRowData } from '../../grid-api.utils';
import { GRID_EVENT } from '../../grid-public-events';
import { isNil, isString, property } from 'lodash';
import { Subscription } from 'rxjs';
import { GridSelectedRowChangeEvent } from '../../grid-events';
import { createSimpleGridModel } from '../../state/simple-grid-state';
import { FocusedCell, GridModel } from '../../state';
import {
  GRID_FEATURE,
  GridFeature,
  GridFeatureInitParams,
  GridFeatureReadyParams,
} from '../feature.interfaces';

@Directive({
  standalone: false,
  selector: '[ghGridModelData],[ghGridImmutableData]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridModelDataDirective,
    multi: true,
  }],
  // We define all inputs via @Directive decorator, because we have to handle all inputs in order.
  // We use ngOnChanges to handle all inputs in order.
  inputs: [
    'modelData:ghGridModelData',
    'immutableData:ghGridImmutableData',
    'focusedCell',
    'selectedRow',
  ],
})
export class GridModelDataDirective implements GridFeature, OnChanges, DoCheck {
  /**
   * Easy way to set identity property.
   */
  @Input('ghGridImmutableDataId') idProperty: string | TransformFn<any, any>;

  @Output() focusedCellChange = new EventEmitter<FocusedCell>();
  @Output() selectedRowChange = new EventEmitter<GridSelectedRowChangeEvent<any>>();

  order = 10;
  immutableData: any;
  modelData: any;

  private _focusedCell: FocusedCell;
  private _selectedRow: any;
  private _data: any;
  private _lastImmutableData: any;
  private _lastModelData: any;
  private model: GridModel;
  private api: GridApi;

  private dataSubscription?: Subscription;
  private editableSubscription?: Subscription;
  private initialized: boolean = false;
  private modelBasedMode = true;

  /**
   * This flag indicates that selection was requested by setting input parameter,
   * but was not applied to the grid yet.
   * This is necessary to distinguish model-based requests to select row from user-based (like click on a row).
   * We have to guarantee one-way data flow without any side effect.
   * So we always have to take selected row from the model.
   */
  private selectionInProgress: boolean = false;

  setData(data: any): void {
    this._data = data;

    if (this.model) {
      this.model.setData(data);
    }
  }

  setFocusedCell(cell: FocusedCell): void {
    if (cell !== this._focusedCell) {
      this._focusedCell = cell;

      this.goToFocusedCell();
    }
  }

  setSelectedRow(id: any): void {
    if (id !== this._selectedRow) {
      this._selectedRow = id;

      this.goToSelectedRow();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { immutableData, modelData, focusedCell, selectedRow } = changes;
    if (!this.initialized) {
      if (immutableData && modelData) {
        throw new Error('GridModelDataDirective: this directive accepts data ' +
          'either from ghGridModelData or ghGridImmutableData, but not from both');
      } else if (immutableData) {
        this.modelBasedMode = false;
      }
    }
    const data = immutableData || modelData;
    if (data && data.currentValue !== undefined && data.currentValue !== null) {
      this.setData(data.currentValue);
    }
    if (focusedCell) {
      this.setFocusedCell(focusedCell.currentValue);
    }
    if (selectedRow) {
      this.setSelectedRow(selectedRow.currentValue);
    }
  }

  ngDoCheck(): void {
    if (this.immutableData !== this._lastImmutableData) {
      this._lastImmutableData = this.immutableData;
      if (this.immutableData !== undefined && this.immutableData !== null) {
        this.setData(this.immutableData);
      }
    }
    if (this.modelData !== this._lastModelData) {
      this._lastModelData = this.modelData;
      if (this.modelData !== undefined && this.modelData !== null) {
        this.setData(this.modelData);
      }
    }
  }

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  enhanceGridOptions(options: GridOptions): void {
    const contextModel: GridModel = options.context;
    const hasValidModel = !isNil(contextModel) && isObservable(contextModel.rowData);
    
    if (this.modelBasedMode && !hasValidModel) {
      if (this.idProperty || options.getRowId) {
        this.modelBasedMode = false;
      } else {
        throw new Error('GridModelDataDirective: GridModel is required by ghGridModelData directive. ' +
          'Setup GridModel using Model-based GridDefinition');
      }
    }
    
    if (this.modelBasedMode && hasValidModel) {
      this.model = contextModel;
    } else if (this.idProperty) {
      const getId = isString(this.idProperty) ? property(this.idProperty) : this.idProperty;
      this.model = createSimpleGridModel(getId);
      options.getRowId = (params) => String(getId(params.data));
    } else if (options.getRowId) {
      const getRowId = options.getRowId;
      this.model = createSimpleGridModel((data: any) => getRowId({ data } as any));
    } else {
      throw new Error('GridModelDataDirective: GridOptions.getRowId should be implemented ' +
        'when immutable data is provided');
    }

    this.dataSubscription = this.model.rowData
      .subscribe((rowData: any[]) => {
        const validRowData = Array.isArray(rowData) ? rowData : (rowData || []);
        if (this.initialized) {
          setGridRowData(this.api, validRowData);
        } else {
          options.rowData = validRowData;
        }
      });
    if (!options.rowData) {
      options.rowData = [];
    }

    if (this._data !== undefined && this._data !== null && Array.isArray(this._data)) {
      this.model.setData(this._data);
    }
  }

  onGridReady(params: GridFeatureReadyParams): void {
    this.api = params.api;
    this.initialized = true;

    const { model, api } = this;

    api.addEventListener(GRID_EVENT.rowSelected, this.onRowSelected.bind(this));

    this.editableSubscription = model.editable.pipe(
      distinctUntilChanged$())
      .subscribe(() => {
        if (this.isAnyCellEditing(api)) {
          return;
        }
        api.refreshCells();
      });

    this.bindEvents();

    if (this._data !== undefined && this.model) {
      this.model.setData(this._data);
    }

    this.goToFocusedCell();
    this.goToSelectedRow();
  }

  onDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    if (this.editableSubscription) {
      this.editableSubscription.unsubscribe();
    }
  }

  private goToFocusedCell(): void {
    const { api, _focusedCell: cell } = this;

    if (cell && api) {
      if (this.isAnyCellEditing(api)) {
        return;
      }
      const node = api.getRowNode(cell.id);
      if (node && node.rowIndex != null) {
        api.ensureIndexVisible(node.rowIndex);
        api.setFocusedCell(node.rowIndex, cell.field);
      }
    }
  }

  private isAnyCellEditing(api: GridApi): boolean {
    const getEditingCells = (api as GridApi & { getEditingCells?: () => unknown[] }).getEditingCells;
    const editing = getEditingCells && getEditingCells.call(api);
    return Array.isArray(editing) && editing.length > 0;
  }

  private goToSelectedRow(): void {
    const { api, _selectedRow: row } = this;

    if (row && api) {
      const node = api.getRowNode(row);
      if (node && !node.isSelected()) {
        // wait until selection will be handled by grid
        this.selectionInProgress = true;
        node.setSelected(true);
        if (node.rowIndex != null) {
          api.ensureIndexVisible(node.rowIndex);
        }
      }
    }
  }

  private bindEvents(): void {
    const { api, model } = this;
    api.addEventListener('cellFocused', (event: CellFocusedEvent) => {
      const { rowIndex, column } = event;
      if (rowIndex == null || column == null || typeof column === 'string') {
        return;
      }
      const col = column as Column;
      const node = api.getDisplayedRowAtIndex(rowIndex);

      if (isNil(node)) {
        // can be null for pinned rows
        // currently focusing of pinned rows is not supported
        return;
      }

      const id = model.getEntityId(node.data);
      const field = col.getColDef().field!;

      const cell = this._focusedCell = { id, field };
      this.focusedCellChange.emit(cell);
    });
  }

  private onRowSelected({ node }: { node: IRowNode }): void {
    if (this.selectionInProgress) {
      // selection was handled by grid => typical grid selection flow can continue now.
      this.selectionInProgress = false;
      return;
    }
    if (node.isSelected()) {
      const { _selectedRow: selectedRow } = this;
      if (node.id !== selectedRow) {
        // when selection was modified we emit event
        this.selectedRowChange.emit({ id: node.id, node, data: node.data });
        // always reset selection to relay on one-way data flow (avoid any side effects)
        if (selectedRow) {
          this.api.getRowNode(String(selectedRow))?.setSelected(true);
        } else {
          node.setSelected(false);
        }
      }
    } else {
      const ri = node.rowIndex;
      const nextNode = getDisplayedNeighborRowForSelection(this.api, ri);
      if (nextNode && nextNode !== node) {
        nextNode.setSelected(true);
      } else if (isNil(nextNode)) {
        this.selectedRowChange.emit({});
      }
    }
  }
}
